import { useCallback, useRef, useState, type ReactNode } from "react";
import { PrismButton, PrismDialog } from "@prism-ui/react";

export interface ConfirmOptions {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: "primary" | "danger" | "success";
}

/**
 * An in-app replacement for window.confirm, which pops out of the app as a bare browser dialog.
 *
 *   const { confirm, confirmDialog } = useConfirm();
 *   if (!(await confirm({ title: "Accept all?", message: "…" }))) return;
 *   …
 *   return <>{…}{confirmDialog}</>;
 *
 * Render confirmDialog as a sibling of any open PrismDialog (not inside it): Prism dialogs use
 * showModal(), so the one opened last stacks on top.
 */
const useConfirm = () => {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((ok: boolean) => void) | null>(null);

  const settle = useCallback((ok: boolean) => {
    resolver.current?.(ok);
    resolver.current = null;
    setOptions(null);
  }, []);

  const confirm = useCallback((next: ConfirmOptions) => {
    // A second request supersedes an unanswered one, which counts as cancelled.
    resolver.current?.(false);
    setOptions(next);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const confirmDialog = (
    <PrismDialog
      className="confirm-dialog"
      open={!!options}
      close-method="user"
      setAriaLabel={options?.title ?? "Confirm"}
      onPrismClose={() => settle(false)}
    >
      <span slot="header">{options?.title}</span>
      <div className="confirm-dialog-message">{options?.message}</div>
      {/* Labels change per request, so they sit in spans (React would otherwise overwrite the button's markup).
          While closed they are blank so they never duplicate a label elsewhere on the page. */}
      <PrismButton slot="footer" tone="secondary" display="outline" onPrismClick={() => settle(false)}>
        <span>{options ? (options.cancelLabel ?? "Cancel") : ""}</span>
      </PrismButton>
      <PrismButton slot="footer" tone={options?.tone ?? "primary"} onPrismClick={() => settle(true)}>
        <span>{options ? (options.confirmLabel ?? "Confirm") : ""}</span>
      </PrismButton>
    </PrismDialog>
  );

  return { confirm, confirmDialog };
};

export default useConfirm;
